@if (draft(); as node) {
  <div class="properties">
    <header class="properties-header">
      <span class="properties-type">
        <span class="material-symbols-outlined properties-type-icon" aria-hidden="true">{{ typeIcon() }}</span>
        {{ typeLabel() }}
      </span>
      <button type="button" class="delete-btn" (click)="onDelete()" aria-label="Delete node">
        <span class="material-symbols-outlined">delete</span>
      </button>
    </header>

    <!-- Common fields -->
    <label class="field">
      <span class="field-label">Name</span>
      <input
        type="text"
        [ngModel]="node.name"
        (ngModelChange)="patch({ name: $event })"
        placeholder="Node name"
      />
    </label>

    <label class="field">
      <span class="field-label">Description</span>
      <textarea
        rows="2"
        [ngModel]="node.description"
        (ngModelChange)="patch({ description: $event })"
        placeholder="Optional description"
      ></textarea>
    </label>

    <!-- Trigger event type -->
    @if (isTrigger()) {
      <label class="field">
        <span class="field-label">Trigger event type</span>
        <input
          type="text"
          list="wf-trigger-types"
          [ngModel]="node.definition.expression"
          (ngModelChange)="patchDefinition({ expression: $event })"
          placeholder="e.g. ApplyForProgramme"
        />
        <datalist id="wf-trigger-types">
          @for (t of triggerTypes(); track t) {
            <option [value]="t"></option>
          }
        </datalist>
        <span class="field-hint">
          Application-defined string matched against the inbound event's TriggerType.
        </span>
      </label>
    }

    @if (isCondition() || isWaitForTrigger()) {
      <div class="field">
        <span class="field-label">Expression</span>
        <app-expression-editor
          [value]="node.definition.expression || ''"
          (valueChange)="patchDefinition({ expression: $event })"
        />
      </div>
    }

    @if (isTimeDelay()) {
      <fieldset class="field delay-picker">
        <legend class="field-label">Delay</legend>
        <div class="delay-grid">
          <label class="delay-part">
            <span>Days</span>
            <input
              type="number"
              min="0"
              [ngModel]="delayParts().days"
              (ngModelChange)="patchDelayPart('days', $event)"
            />
          </label>
          <label class="delay-part">
            <span>Hours</span>
            <input
              type="number"
              min="0"
              max="23"
              [ngModel]="delayParts().hours"
              (ngModelChange)="patchDelayPart('hours', $event)"
            />
          </label>
          <label class="delay-part">
            <span>Minutes</span>
            <input
              type="number"
              min="0"
              max="59"
              [ngModel]="delayParts().minutes"
              (ngModelChange)="patchDelayPart('minutes', $event)"
            />
          </label>
          <label class="delay-part">
            <span>Seconds</span>
            <input
              type="number"
              min="0"
              max="59"
              [ngModel]="delayParts().seconds"
              (ngModelChange)="patchDelayPart('seconds', $event)"
            />
          </label>
        </div>
        <span class="field-hint">
          Serialised as <code>{{ node.definition.delay || '00:00:00' }}</code>
        </span>
      </fieldset>
    }

    <!-- Action: Send Email -->
    @if (isSendEmail()) {
      <label class="field">
        <span class="field-label">Email template</span>
        @if (availableEmailTemplates().length > 0) {
          <select
            [ngModel]="node.definition.emailTemplateId"
            (ngModelChange)="patchDefinition({ emailTemplateId: $event })"
          >
            <option value="">Select template…</option>
            @for (t of availableEmailTemplates(); track t.id) {
              <option [value]="t.id">{{ t.name }}</option>
            }
          </select>
        } @else {
          <input
            type="text"
            [ngModel]="node.definition.emailTemplateId"
            (ngModelChange)="patchDefinition({ emailTemplateId: $event })"
            placeholder="Email template id"
          />
          <span class="field-hint">No template catalog provided — paste the template id.</span>
        }
      </label>
      <label class="field">
        <span class="field-label">Payload (supports placeholders)</span>
        <textarea
          #payloadTa
          rows="3"
          [ngModel]="node.definition.payload"
          (ngModelChange)="patchDefinition({ payload: $event })"
          placeholder='e.g. {"firstName": "{{ "{{Application.FirstName}}" }}"}'
        ></textarea>
      </label>
      <app-placeholder-picker
        [metadataKeys]="node.requiredMetadata"
        (insert)="insertIntoPayload($event)"
      />
    }

    <!-- Action: Webhook -->
    @if (isWebhook()) {
      <label class="field">
        <span class="field-label">URL</span>
        <input
          #urlInput
          type="text"
          [ngModel]="node.definition.url"
          (ngModelChange)="patchDefinition({ url: $event })"
          placeholder="https://…"
        />
      </label>
      <app-placeholder-picker
        [metadataKeys]="node.requiredMetadata"
        (insert)="insertIntoUrl($event)"
      />
      <label class="field">
        <span class="field-label">Method</span>
        <select
          [ngModel]="node.definition.method || 'POST'"
          (ngModelChange)="patchDefinition({ method: $event })"
        >
          @for (m of httpMethods; track m) {
            <option [value]="m">{{ m }}</option>
          }
        </select>
      </label>
      <label class="field">
        <span class="field-label">Payload</span>
        <textarea
          #payloadTa
          rows="3"
          [ngModel]="node.definition.payload"
          (ngModelChange)="patchDefinition({ payload: $event })"
        ></textarea>
      </label>
      <app-placeholder-picker
        [metadataKeys]="node.requiredMetadata"
        (insert)="insertIntoPayload($event)"
      />
      <label class="field">
        <span class="field-label">Response type</span>
        <select
          [ngModel]="node.definition.responseType"
          (ngModelChange)="patchDefinition({ responseType: $event })"
        >
          @for (rt of responseTypes; track rt) {
            <option [value]="rt">{{ rt }}</option>
          }
        </select>
      </label>
      @if (showResponseTemplate()) {
        <app-response-template-builder
          [value]="node.definition.responseTemplate || ''"
          [responseType]="node.definition.responseType"
          (valueChange)="patchDefinition({ responseTemplate: $event })"
        />
      }
    }

    <!-- Required metadata -->
    <app-required-metadata-editor
      [items]="node.requiredMetadata"
      (itemsChange)="patch({ requiredMetadata: $event })"
    />

    @if (suggestedMetadata().length > 0) {
      <div class="suggested-metadata">
        <div class="suggested-metadata-header">
          <span class="suggested-metadata-title">Referenced but not declared</span>
          <button type="button" class="link" (click)="acceptAllSuggestedMetadata()">
            Add all
          </button>
        </div>
        <div class="suggested-metadata-chips">
          @for (key of suggestedMetadata(); track key) {
            <button
              type="button"
              class="suggested-chip"
              (click)="acceptSuggestedMetadata(key)"
              [attr.title]="'Add ' + key + ' to Required Metadata'"
            >
              + {{ key }}
            </button>
          }
        </div>
      </div>
    }

    <!-- Connectivity -->
    @if (node.type !== WorkflowNodeType.Terminator) {
      <label class="field">
        <span class="field-label">{{ isCondition() ? 'Next (true)' : 'Next' }}</span>
        <select
          [ngModel]="node.successorTrue"
          (ngModelChange)="patch({ successorTrue: $event })"
        >
          <option value="">— none —</option>
          @for (opt of successorOptions(); track opt.id) {
            <option [value]="opt.id">{{ opt.name || 'Untitled' }}</option>
          }
        </select>
      </label>

      @if (isCondition()) {
        <label class="field">
          <span class="field-label">Next (false)</span>
          <select
            [ngModel]="node.successorFalse"
            (ngModelChange)="patch({ successorFalse: $event })"
          >
            <option value="">— none —</option>
            @for (opt of successorOptions(); track opt.id) {
              <option [value]="opt.id">{{ opt.name || 'Untitled' }}</option>
            }
          </select>
        </label>
      }
    }
  </div>
} @else {
  <div class="properties-empty">
    <span class="material-symbols-outlined">tune</span>
    <p>Select a node to edit its properties.</p>
  </div>
}
